import type { FairState, Schedule } from './schema';
import { MIN, toTs } from './time';

export interface RoundWindow {
  start: number;
  end: number;
}

export interface PhaseResult {
  /** Effective state: the manual override when mode is manual, else `auto`. */
  state: FairState;
  /** State the clock alone would give. */
  auto: FairState;
  cs: number;
  ce: number;
  /** Round windows with the delay already applied. */
  R: RoundWindow[];
  /** False when any schedule time is missing or malformed. */
  valid: boolean;
  /** Index of the running round, or -1. Only set during `sessions`. */
  round: number;
  /** Index of the next round when between rounds, or -1. */
  next: number;
  now: number;
  teamsOpen: boolean;
  webinarOpen: boolean;
}

export type PhaseInput = Pick<
  Schedule,
  'date' | 'convStart' | 'convEnd' | 'rounds' | 'mode' | 'manualState' | 'delay'
>;

/**
 * The core event logic.
 *
 * - before:   until the central gathering starts
 * - keynote:  gathering start until round 1 starts (delay included). May run past `convEnd`.
 * - sessions: round 1 start until round 3 end. Teams stays open through breaks.
 * - ended:    after round 3 ends
 *
 * `delay` shifts the rounds only, never the gathering. Manual mode overrides the clock.
 */
export function computePhase(s: PhaseInput, now: number): PhaseResult {
  const cs = toTs(s.date, s.convStart);
  const ce = toTs(s.date, s.convEnd);
  const dl = (Number(s.delay) || 0) * MIN;
  const R = s.rounds.map((r) => ({
    start: toTs(s.date, r.start) + dl,
    end: toTs(s.date, r.end) + dl,
  }));
  const valid = [cs, ce, ...R.flatMap((r) => [r.start, r.end])].every(Number.isFinite);

  let auto: FairState = 'before';
  if (valid) {
    if (now < cs) auto = 'before';
    else if (now < R[0]!.start) auto = 'keynote';
    else if (now < R[2]!.end) auto = 'sessions';
    else auto = 'ended';
  }

  const state = s.mode === 'manual' ? s.manualState : auto;
  let round = -1;
  let next = -1;
  if (state === 'sessions' && valid) {
    round = R.findIndex((r) => now >= r.start && now < r.end);
    if (round < 0) next = R.findIndex((r) => now < r.start);
  }

  return {
    state,
    auto,
    cs,
    ce,
    R,
    valid,
    round,
    next,
    now,
    teamsOpen: state === 'sessions',
    webinarOpen: state === 'keynote',
  };
}
